import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Label } from 'semantic-ui-react';
import { changeDocTitle } from 'src/components/helpers';
import AGTable from 'src/components/table/aggrid';
import {
  captureAgGridViewState,
  restoreAgGridViewState,
  getActiveFilters,
} from 'src/components/table/helpers';
import weddingRequestService from 'src/services/api/weddingRequestService';
import { WeddingRequestsRow } from 'src/components/common/rows/WeddingRequestsRow';
import styled from '@emotion/styled';
import { adminTableStyles, AdminTableContent } from 'src/assets/styles/components/AdminTable';
import WeddingRequestFacilitators from 'src/containers/wedding-request/admin/weddingFacilitators';
import AdminWeddingRequestAnswers from 'src/containers/wedding-request/admin/adminWeddingRequestAnswers';

const WeddingRequestTable = styled('section')`
  ${adminTableStyles}
`;

const COLUMN_NAME_MAP = {
  createdAt: 'Fecha',
  his_name: 'Nombre El',
  her_name: 'Nombre Ella',
  maleLeaderApproved: 'Aval líder él',
  femaleLeaderApproved: 'Aval líder ella',
  FacilitatorGuest: 'Facilitador',
  christian_ceremony_date: 'Fecha boda',
  ceremony_time: 'Hora',
  pastorAssigned: 'Pastor asignado',
  ceremony_location: 'Lugar',
};

const AllWeddingRequests = (props) => {
  const [allWeddingRequests, set_allWeddingRequests] = useState([]);
  const [loading, set_loading] = useState(true);
  const [showWeddingRequestForm, set_showWeddingRequestForm] = useState(null);
  const [allWeddingFacilitators, set_allWeddingFacilitators] = useState([]);
  const [savedFilterModel, set_savedFilterModel] = useState(null);
  const [activeFilters, set_activeFilters] = useState({});
  const gridRef = useRef(null);
  const viewStateRef = useRef(null);

  useEffect(() => {
    changeDocTitle('Solicitudes de Matrimonio');
    getInfo();
  }, []);

  const syncActiveFilters = useCallback((api) => {
    if (!api) {
      set_activeFilters({});
      return;
    }
    set_activeFilters(getActiveFilters(api, COLUMN_NAME_MAP));
  }, []);

  const openWeddingRequestForm = useCallback((id) => {
    viewStateRef.current = captureAgGridViewState(gridRef.current?.api);
    set_showWeddingRequestForm(id);
  }, []);

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
    }

    if (savedFilterModel) {
      params.api.setFilterModel(savedFilterModel);
      params.api.onFilterChanged();
    }

    syncActiveFilters(params.api);
    restoreAgGridViewState(params.api, viewStateRef.current);
  };

  const onFilterChanged = () => {
    if (gridRef.current?.api) {
      const model = gridRef.current.api.getFilterModel();
      set_savedFilterModel(Object.keys(model || {}).length ? model : null);
      syncActiveFilters(gridRef.current.api);
    }
  };

  function getInfo() {
    set_loading(true);
    weddingRequestService
      .getAllWeddingRequests()
      .then((response) => {
        if (response.data.success) {
          set_loading(false);
          set_allWeddingRequests(response.data.allWeddingRequests);
        } else {
          set_loading(null);
        }
      })
      .catch((error) => {
        set_loading(false);
      });
  }

  if (showWeddingRequestForm) {
    return (
      <AdminWeddingRequestAnswers
        set_showWeddingRequestForm={set_showWeddingRequestForm}
        answers={allWeddingRequests.find((form) => form.id === showWeddingRequestForm)}
        getInfo={getInfo}
        allWeddingFacilitators={allWeddingFacilitators}
      />
    );
  }

  return (
    <WeddingRequestTable>
      <AdminTableContent>
        <div>
          <Button primary disabled={loading} onClick={getInfo}>
            Recargar
          </Button>
        </div>
        <WeddingRequestFacilitators
          allWeddingFacilitators={allWeddingFacilitators}
          set_allWeddingFacilitators={set_allWeddingFacilitators}
        />
        {Object.keys(activeFilters).length > 0 && (
          <div style={{ marginTop: '16px', marginBottom: '8px' }}>
            <strong style={{ marginRight: '8px' }}>Filtros activos:</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {Object.entries(activeFilters).map(([column, value]) => (
                <Label key={column} color="blue" style={{ margin: '4px 0', fontWeight: '400' }}>
                  <strong>{column}:</strong> {value}
                </Label>
              ))}
            </div>
          </div>
        )}
      </AdminTableContent>
      <div className="table">
        {!!loading && <div className="loading">Cargando...</div>}
        <AGTable
          rowData={allWeddingRequests}
          rowHeight={50}
          tableRef={gridRef}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          columnDefs={WeddingRequestsRow({
            set_showWeddingRequestForm: openWeddingRequestForm,
            loading,
          })}
          suppressDragLeaveHidesColumns={false}
        />
      </div>
    </WeddingRequestTable>
  );
};

export default AllWeddingRequests;
